import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useStripe,
  useElements,
  CardElement,
  Elements
} from '@stripe/react-stripe-js';
import { stripePromise } from '../config/stripe';
import { createPaymentIntent, confirmPayment } from '../api/services/applicationService';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#424770',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#9e2146',
      iconColor: '#9e2146'
    }
  },
  hidePostalCode: false
};

interface PaymentFormProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const PaymentFormContent: React.FC<PaymentFormProps> = ({ onBack, onSuccess }) => {
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !applicationId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        setError('Card element not found');
        setLoading(false);
        return;
      }

      const { error: paymentError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (paymentError) {
        setError(paymentError.message || 'An error occurred');
        setLoading(false);
        return;
      }

      if (!paymentMethod) {
        setError('Failed to create payment method');
        setLoading(false);
        return;
      }

      const paymentIntentResponse = await createPaymentIntent(applicationId, {
        paymentMethodId: paymentMethod.id,
        amount: 2100,
      });

      const { clientSecret } = paymentIntentResponse.data;

      const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        setLoading(false);
        return;
      }

      await confirmPayment(applicationId, paymentIntentResponse.data.paymentIntentId);

      setShowSuccess(true);
      setLoading(false);

      setTimeout(() => {
        navigate(`/application/${applicationId}`);
      }, 3000);

    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.response?.data?.message || err.message || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="rounded-xl bg-white shadow-sm">
        <div className="py-12 text-center">
          <div className="mb-4">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto text-success"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                fill="currentColor"
              />
            </svg>
          </div>

          <h3 className="mb-3 text-lg font-semibold text-success">Payment Successful!</h3>
          <p className="mb-4 text-sm text-gray-600">
            Your $21 verification fee has been processed successfully.
            You can now continue with your application.
          </p>
          <p className="text-sm text-muted">
            Redirecting you to continue your application...
          </p>

          <div className="mt-4">
            <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white shadow-sm">
      <div className="border-b border-gray-100 px-6 py-4">
        <h4 className="font-semibold text-primary">Payment Information</h4>
      </div>
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          <div className="mx-auto max-w-md">
            {error && (
              <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}

            <div className="mb-6">
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Card Information</label>
              <div className="rounded-lg border border-gray-300 px-4 py-4 transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <CardElement options={CARD_ELEMENT_OPTIONS} />
              </div>
              <p className="mt-1.5 text-xs text-muted">
                Enter your card number, expiry date, CVC, and postal code.
              </p>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                onClick={onBack}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:opacity-50"
                disabled={!stripe || loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Processing...
                  </span>
                ) : (
                  'Pay $21'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const PaymentForm: React.FC<PaymentFormProps> = ({ onBack, onSuccess }) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent onBack={onBack} onSuccess={onSuccess} />
    </Elements>
  );
};

export default PaymentForm;
